import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins, faCartShopping, faHandHoldingDollar } from '@fortawesome/free-solid-svg-icons';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { getItemImageSrc } from '../../../utils/itemUtils';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import { Item } from '../../types/Item';
import styles from './Market.module.css';

interface Vendor {
  id: number;
  name: string;
  description: string;
  location?: string;
  isOrange?: boolean;
}

interface VendorInventory {
  id: number;
  vendorId: number;
  itemId: number;
  stock: number;
  price: number;
  itemCategory: string;
  itemSubCategory: string;
  img?: string;
  itemDetails: Item;
  goodsIndex?: number;
}

// All recognized categories in display order — must match `categories.ts`
const ALL_CATEGORIES = ['Weapon', 'Armor', 'Shield', 'Accessory', 'Potion', 'Material', 'Knowledge', 'Rune', 'Miscellaneous'];

const SUB_CATEGORY_MAP: Record<string, string[]> = {
  Weapon:    ['Axe', 'Mace', 'Sword', 'Staff', 'Dagger', 'Chain'],
  Armor:     ['Head', 'Shoulder', 'Torso', 'Hand', 'Leg', 'Feet'],
  Accessory: ['Cloak', 'Neck', 'Ring', 'Armring', 'Amulet'],
  Knowledge: ['Tome', 'Scroll', 'Book'],
  Rune:      ['green', 'orange', 'blue', 'red', 'aqua', 'yellow', 'gray', 'purple'],
};

const getItemSubCategoryValue = (item: VendorInventory): string => {
  if (item.itemCategory === 'Weapon')    return (item.itemDetails as any).weaponType    || item.itemSubCategory || '';
  if (item.itemCategory === 'Armor')     return (item.itemDetails as any).armorType     || item.itemSubCategory || '';
  if (item.itemCategory === 'Accessory') return (item.itemDetails as any).accessoryType || item.itemSubCategory || '';
  if (item.itemCategory === 'Rune')      return (item.itemDetails as any).runeColor      || item.itemSubCategory || '';
  if (item.itemCategory === 'Knowledge') return (item.itemDetails as any).knowledgeType  || item.itemSubCategory || '';
  return item.itemSubCategory || '';
};

interface SellItem {
  id: number;
  name: string;
  img?: string;
  slot?: string;
  itemCategory: string;
  requiredLevel?: number;
  value: number;
  quantity: number;
  rarity?: string;
  weaponType?: string;
  armorType?: string;
  accessoryType?: string;
  [key: string]: any;
}

const VENDOR_LORE: Record<number, { title: string; lore: string }> = {
  '-1': { title: "Orange's Cart", lore: "A cheerful wandering merchant who appears without warning, bearing rare and unusual wares. Don't let the cheerful appearance fool you, her prices reflect the rarity of her goods." },
} as any;

const Marketplace: React.FC = () => {
  const { mercenary, mercenaryId } = useMercenary();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  const [items, setItems] = useState<VendorInventory[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(true);
  const [loadingItems, setLoadingItems] = useState<boolean>(false);
  const [orangeMaxVisits, setOrangeMaxVisits] = useState<number>(0);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [sellMode, setSellMode] = useState<boolean>(false);
  const [sellItems, setSellItems] = useState<SellItem[]>([]);
  const [loadingSellItems, setLoadingSellItems] = useState<boolean>(false);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  };

  // Compute which categories this vendor actually sells
  const availableCategories = useMemo(() => {
    if (selectedVendor?.isOrange) {
      // Orange shows all in one view, no category filtering
      return [];
    }
    const cats = new Set(items.map(i => i.itemCategory).filter(Boolean));
    return ALL_CATEGORIES.filter(c => cats.has(c));
  }, [items, selectedVendor]);

  // Compute which subcategories are non-empty for the selected tab
  const availableSubCategories = useMemo(() => {
    if (!selectedTab || selectedVendor?.isOrange) return [];
    const allSubs = SUB_CATEGORY_MAP[selectedTab] || [];
    if (allSubs.length === 0) return [];
    const vendorSubs = new Set(
      items
        .filter(i => i.itemCategory === selectedTab)
        .map(i => getItemSubCategoryValue(i))
        .filter(Boolean)
    );
    return allSubs.filter(s => vendorSubs.has(s));
  }, [items, selectedTab, selectedVendor]);

  // Auto-clear tab/subcategory only when switching vendors
  // (no auto-select: default is to show all items)

  // Auto-clear subcategory if it's no longer valid for the new tab
  useEffect(() => {
    if (selectedSubCategory && !availableSubCategories.includes(selectedSubCategory)) {
      setSelectedSubCategory(null);
    }
  }, [availableSubCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchVendors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVendors = () => {
    setLoadingVendors(true);
    axios
      .get('/api/market/vendors', { withCredentials: true })
      .then((response) => {
        setVendors(response.data);
        if (response.data.length > 0) {
          handleVendorSelect(response.data[0]);
        }
        setLoadingVendors(false);
      })
      .catch((error) => {
        console.error('Error fetching vendors:', error);
        displayToast('Failed to load vendors.', 'error');
        setLoadingVendors(false);
      });
  };

  const handleVendorSelect = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setSelectedTab('');
    setSelectedSubCategory(null);
    setItems([]);
    setSellMode(false);
    if (vendor.isOrange) {
      fetchOrangeItems();
    } else {
      fetchVendorItems(vendor.id);
    }
  };

  const fetchVendorItems = (vendorId: number, silent = false) => {
    if (!silent) setLoadingItems(true);
    axios
      .get(`/api/market/vendors/${vendorId}/items`, { withCredentials: true })
      .then((response) => {
        setItems(response.data);
        if (!silent) setLoadingItems(false);
      })
      .catch((error) => {
        console.error('Error fetching vendor items:', error);
        displayToast('Failed to load items.', 'error');
        if (!silent) setLoadingItems(false);
      });
  };

  const fetchOrangeItems = (silent = false) => {
    if (!silent) setLoadingItems(true);
    axios
      .get('/api/city-buildings/orange-merchant', { withCredentials: true })
      .then((response) => {
        const { goods, maxVisitsPerMercenary } = response.data;
        setOrangeMaxVisits(maxVisitsPerMercenary || 0);
        const mapped: VendorInventory[] = (goods || []).map((good: any) => ({
          id: good.goodsIndex,
          vendorId: -1,
          itemId: good.itemId || 0,
          stock: good.stock,
          price: good.price,
          itemCategory: good.itemDetails?.itemCategory || 'Misc',
          itemSubCategory: good.itemDetails?.slot || '',
          goodsIndex: good.goodsIndex,
          itemDetails: good.itemDetails || { name: good.name },
        }));
        setItems(mapped);
        if (!silent) setLoadingItems(false);
      })
      .catch((error) => {
        console.error('Error fetching Orange items:', error);
        displayToast("Failed to load Orange's goods.", 'error');
        if (!silent) setLoadingItems(false);
      });
  };

  const handleTabSelect = (tabName: string) => {
    // Clicking the active tab deselects it (show all)
    if (selectedTab === tabName) {
      setSelectedTab('');
    } else {
      setSelectedTab(tabName);
    }
    setSelectedSubCategory(null);
  };

  const handleSubCategorySelect = (subCategory: string) => {
    setSelectedSubCategory(subCategory === selectedSubCategory ? null : subCategory);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = '/img/no_img.png';
  };

  const handleVendorImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    (e.currentTarget as HTMLImageElement).style.display = 'none';
  };

  const fetchSellItems = () => {
    if (!mercenaryId) return;
    setLoadingSellItems(true);
    axios
      .get(`/api/inventory/${mercenaryId}`, { withCredentials: true })
      .then((res) => {
        const raw = res.data;
        // Inventory endpoint returns a flat array of equipped + unequipped items
        const inv: any[] = Array.isArray(raw) ? raw : (raw.inventory || []);
        const sellable = inv
          .filter((i: any) => !i.equipped && i.quantity > 0)
          .sort((a: any, b: any) => {
            const catOrder = ['Weapon','Armor','Shield','Accessory','Potion','Material','Rune','Knowledge'];
            const ca = catOrder.indexOf(a.itemCategory ?? '');
            const cb = catOrder.indexOf(b.itemCategory ?? '');
            if (ca !== cb) return (ca === -1 ? 99 : ca) - (cb === -1 ? 99 : cb);
            return (a.requiredLevel || 0) - (b.requiredLevel || 0);
          });
        setSellItems(sellable);
        setLoadingSellItems(false);
      })
      .catch((err) => {
        console.error('Error fetching sell inventory:', err);
        displayToast('Failed to load your inventory.', 'error');
        setLoadingSellItems(false);
      });
  };

  const handleToggleSellMode = () => {
    if (!sellMode) {
      fetchSellItems();
    }
    setSellMode(prev => !prev);
  };

  const handleSellItem = (item: SellItem, qty: number) => {
    axios
      .post('/api/market/sell', { mercenaryId, itemId: item.id, quantity: qty }, { withCredentials: true })
      .then((res) => {
        displayToast(`Sold ${qty}x ${item.name} for ${res.data.totalEarned.toLocaleString()} silver!`, 'success');
        if (res.data.torrenStock > 0) {
          displayToast(`${res.data.torrenStock}x ${item.name} resold by Torren!`, 'success');
          if (selectedVendor?.id === 8) {
            fetchVendorItems(8, true);
          }
        }
        if (item.itemInstanceId) {
          // Equippable items: each row is a separate instance with quantity 1.
          // Remove exactly `qty` rows by base item id instead of decrementing all.
          setSellItems(prev => {
            let remaining = qty;
            return prev.filter(i => {
              if (i.id === item.id && remaining > 0) {
                remaining--;
                return false;
              }
              return true;
            });
          });
        } else {
          setSellItems(prev =>
            prev
              .map(i => i.id === item.id ? { ...i, quantity: i.quantity - qty } : i)
              .filter(i => i.quantity > 0)
          );
        }
      })
      .catch((err) => {
        console.error('Error selling item:', err);
        const msg = err?.response?.data?.message || 'Failed to sell item.';
        displayToast(msg, 'error');
      });
  };

  const handleBuyItem = (item: VendorInventory) => {
    if ((mercenary?.silver || 0) < item.price) {
      displayToast('Not enough silver!', 'error');
      return;
    }

    if (selectedVendor?.isOrange) {
      axios
        .post(
          '/api/city-buildings/orange-merchant/buy',
          { goodsIndex: item.goodsIndex },
          { withCredentials: true }
        )
        .then(() => {
          displayToast(`Purchased ${item.itemDetails.name} from Orange!`, 'success');
          setItems(prev =>
            prev
              .map(i => (i.goodsIndex === item.goodsIndex ? { ...i, stock: i.stock - 1 } : i))
              .filter(i => i.stock > 0)
          );
        })
        .catch((error) => {
          console.error('Error buying from Orange:', error);
          const msg = error?.response?.data?.message || 'Failed to buy item.';
          displayToast(msg, 'error');
        });
      return;
    }

    axios
      .post(
        '/api/market/buy',
        {
          mercenaryId,
          vendorId: selectedVendor?.id,
          itemId: item.itemId,
          itemType: item.itemCategory,
        },
        { withCredentials: true }
      )
      .then(() => {
        displayToast(`Purchased ${item.itemDetails.name}!`, 'success');
        setItems(prev =>
          prev
            .map(i => (i.id === item.id ? { ...i, stock: i.stock - 1 } : i))
            .filter(i => i.stock > 0)
        );
      })
      .catch((error) => {
        console.error('Error buying item:', error);
        displayToast('Failed to buy item.', 'error');
      });
  };

  const filteredItems = items
    .filter((item) => {
      if (selectedVendor?.isOrange) return true;
      // No tab selected = show all
      if (selectedTab && item.itemCategory !== selectedTab) return false;
      if (selectedSubCategory) {
        const sub = getItemSubCategoryValue(item);
        if (sub !== selectedSubCategory) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const levelA = (a.itemDetails as any).requiredLevel || 0;
      const levelB = (b.itemDetails as any).requiredLevel || 0;
      if (levelA !== levelB) return levelA - levelB;
      return a.price - b.price;
    });

  const renderStatsCell = (item: VendorInventory) => {
    const d = item.itemDetails as any;
    return (
      <div className={styles.statsContent}>
        {d.minDamage > 0       && <span>DMG: {d.minDamage}–{d.maxDamage}</span>}
        {(d.armorValue > 0 || d.absorption > 0) && <span>Armor: {d.armorValue || d.absorption}</span>}
        {d.blockValue > 0      && <span>Block: {d.blockValue}</span>}
        {d.potionEffect        && <span>Effect: {d.potionEffect}</span>}
        {d.bonuses             && <span>{d.bonuses}</span>}
        {d.materialType        && <span>Material: {d.materialType}</span>}
        {d.enchantModifiers    && <span>Enchant: {d.enchantModifiers}</span>}
      </div>
    );
  };

  const vendorPortraitSrc = selectedVendor
    ? selectedVendor.isOrange
      ? '/img/vendors/orange.png'
      : `/img/vendors/vendor_${selectedVendor.id}.png`
    : '';

  return (
    <div className={styles.marketContainer}>
      {toastMessage && <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage(null)} />}

      {/* ── Page Header ── */}
      <div className={styles.headerSection}>
        <div className={styles.marketTitle}>Marketplace</div>
        <div className={styles.subTitle}>
          Current Silver:{' '}
          <span className={styles.silverAmount}>{mercenary?.silver?.toLocaleString() || 0}</span>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className={styles.contentWrapper}>

        {/* ── Left Sidebar: Vendor Select + Portrait ── */}
        <div className={styles.vendorSidebar}>
          <div className={styles.vendorListHeader}>Vendors</div>

          {loadingVendors ? (
            <div className={styles.sidebarLoading}>Loading…</div>
          ) : (
            vendors.map((vendor) => (
              <div
                key={vendor.id}
                className={`${styles.vendorCard} ${selectedVendor?.id === vendor.id ? styles.active : ''} ${vendor.isOrange ? styles.orangeCard : ''}`}
                onClick={() => handleVendorSelect(vendor)}
              >
                <h3 className={styles.vendorCardName}>{vendor.name}</h3>
                {vendor.location && <p className={styles.vendorCardLocation}>{vendor.location}</p>}
              </div>
            ))
          )}

          {/* Vendor Portrait Card */}
          {selectedVendor && (
            <div className={`${styles.vendorPortrait} ${selectedVendor.isOrange ? styles.orangePortrait : ''}`}>
              <div className={styles.vendorPortraitImgWrapper}>
                <img
                  src={vendorPortraitSrc}
                  alt={selectedVendor.name}
                  className={styles.vendorPortraitImg}
                  onError={handleVendorImageError}
                />
                <div className={styles.vendorPortraitPlaceholder}>
                  <span className={styles.vendorPortraitInitial}>
                    {selectedVendor.name.charAt(0)}
                  </span>
                </div>
              </div>
              <div className={styles.vendorPortraitName}>{selectedVendor.name}</div>
              {selectedVendor.location && (
                <div className={styles.vendorPortraitLocation}>{selectedVendor.location}</div>
              )}
              <p className={styles.vendorPortraitLore}>
                {(VENDOR_LORE as any)[selectedVendor.id]?.lore || selectedVendor.description || ''}
              </p>
            </div>
          )}
        </div>

        {/* ── Main Area: Filters + Table ── */}
        <div className={styles.shopArea}>
          {selectedVendor && (
            <>
              {/* Category filter, only categories the vendor sells */}
              {!selectedVendor.isOrange && (
                <div className={styles.filterBar}>
                  {!sellMode && availableCategories.map((cat) => (
                    <button
                      key={cat}
                      className={`${styles.filterButton} ${selectedTab === cat ? styles.active : ''}`}
                      onClick={() => handleTabSelect(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                  {sellMode && <span className={styles.sellModeLabel}><FontAwesomeIcon icon={faHandHoldingDollar} /> Selling to Vendor</span>}
                  <button
                    className={`${styles.modeToggle} ${sellMode ? styles.modeToggleSell : styles.modeToggleBuy}`}
                    onClick={handleToggleSellMode}
                    title={sellMode ? 'Switch to Buy mode' : 'Switch to Sell mode'}
                  >
                    <FontAwesomeIcon icon={sellMode ? faCartShopping : faHandHoldingDollar} />
                    {sellMode ? ' Buy' : ' Sell'}
                  </button>
                </div>
              )}

              {/* Subcategory filter, only subcategories the vendor sells within this tab */}
              {!sellMode && !selectedVendor.isOrange && availableSubCategories.length > 0 && (
                <div className={`${styles.filterBar} ${styles.subFilterBar}`}>
                  {availableSubCategories.map((sub) => (
                    <button
                      key={sub}
                      className={`${styles.filterButton} ${styles.subFilter} ${selectedSubCategory === sub ? styles.active : ''}`}
                      onClick={() => handleSubCategorySelect(sub)}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              )}

              {/* Orange notice */}
              {selectedVendor.isOrange && orangeMaxVisits > 0 && (
                <div className={styles.orangeNotice}>
                  Each mercenary may purchase up to {orangeMaxVisits} item{orangeMaxVisits !== 1 ? 's' : ''} from Orange's cart today.
                </div>
              )}
            </>
          )}

          {/* Sell Mode Table */}
          {sellMode ? (
            loadingSellItems ? (
              <div className={styles.emptyState}>Loading your inventory…</div>
            ) : sellItems.length === 0 ? (
              <div className={styles.emptyState}>You have no items to sell.</div>
            ) : (
              <div className={styles.tableContainer}>
                <div className={styles.sellNotice}>
                  Vendors buy at <strong> 25% of item value</strong>. Equipped items cannot be sold here.
                </div>
                <table className={styles.marketTable}>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Level</th>
                      <th>Type</th>
                      <th>Owned</th>
                      <th>Sell Price</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sellItems.map((item) => {
                      const sellPrice = Math.floor(item.value / 4);
                      const rarityKey = item.rarity ? item.rarity.charAt(0).toUpperCase() + item.rarity.slice(1).toLowerCase() : 'Common';
                      return (
                        <tr key={item.itemInstanceId || item.id} className={styles.marketRow}>
                          <td className={styles.itemCell}>
                            <Tooltip item={item as unknown as Item} align="left">
                              <div className={styles.itemIdentity}>
                                <img
                                  src={getItemImageSrc(item as unknown as Item)}
                                  alt={item.name}
                                  onError={handleImageError}
                                  className={styles.miniIcon}
                                />
                                <span className={styles[`itemQuality${rarityKey}`] || styles.itemQualityCommon}>
                                  {item.name}
                                </span>
                              </div>
                            </Tooltip>
                          </td>
                          <td className={styles.centerCell} style={{ color: (mercenary?.level ?? 0) < (item.requiredLevel || 1) ? 'var(--color-status-critical, #e07070)' : 'inherit' }}>{item.requiredLevel || 1}</td>
                          <td className={styles.centerCell}>{item.slot || item.itemCategory}</td>
                          <td className={`${styles.centerCell} ${styles.ownedQty}`}>{item.quantity}</td>
                          <td className={styles.priceCell}>
                            <div className={styles.priceWrapper}>
                              <FontAwesomeIcon icon={faCoins} className={styles.currencyIcon} />
                              {sellPrice > 0 ? sellPrice.toLocaleString() : '—'}
                            </div>
                          </td>
                          <td className={styles.actionCell}>
                            <div className={styles.sellActions}>
                              <button
                                className={styles.sellButton}
                                onClick={() => handleSellItem(item, 1)}
                                disabled={sellPrice === 0}
                                title={sellPrice === 0 ? 'Cannot sell this item' : `Sell 1 for ${sellPrice} silver`}
                              >
                                Sell
                              </button>
                              {item.quantity > 1 && (
                                <button
                                  className={`${styles.sellButton} ${styles.sellAllButton}`}
                                  onClick={() => handleSellItem(item, item.quantity)}
                                  disabled={sellPrice === 0}
                                  title={`Sell all ${item.quantity} for ${sellPrice * item.quantity} silver`}
                                >
                                  All
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            /* Buy Mode Table */
            loadingItems ? (
              <div className={styles.emptyState}>Loading wares…</div>
            ) : !selectedVendor ? (
              <div className={styles.emptyState}>Select a vendor to browse their goods.</div>
            ) : filteredItems.length > 0 ? (
              <div className={styles.tableContainer}>
                <table className={styles.marketTable}>
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Level</th>
                      <th>Type</th>
                      <th>Stats</th>
                      <th>Stock</th>
                      <th>Price</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr key={`${item.vendorId}-${item.itemId}-${item.goodsIndex ?? ''}`} className={styles.marketRow}>
                        <td className={styles.itemCell}>
                          <Tooltip item={item.itemDetails} align="left">
                            <div className={styles.itemIdentity}>
                              <img
                                src={getItemImageSrc(item.itemDetails)}
                                alt={item.itemDetails.name}
                                onError={handleImageError}
                                className={styles.miniIcon}
                              />
                              <span className={
                                styles[`itemQuality${item.itemDetails.rarity ? item.itemDetails.rarity.charAt(0).toUpperCase() + item.itemDetails.rarity.slice(1).toLowerCase() : 'Common'}`]
                                || styles.itemQualityCommon
                              }>
                                {item.itemDetails.name}
                              </span>
                            </div>
                          </Tooltip>
                        </td>
                        <td className={styles.centerCell} style={{ color: (mercenary?.level ?? 0) < ((item.itemDetails as any).requiredLevel || 1) ? 'var(--color-status-critical, #e07070)' : 'inherit' }}>{(item.itemDetails as any).requiredLevel || 1}</td>
                        <td className={styles.centerCell}>{(item.itemDetails as any).slot || item.itemSubCategory || item.itemCategory}</td>
                        <td className={styles.statsCell}>{renderStatsCell(item)}</td>
                        <td className={`${styles.centerCell} ${item.stock > 0 ? styles.inStock : styles.outStock}`}>
                          {item.stock > 0 ? item.stock : 'Out of Stock'}
                        </td>
                        <td className={styles.priceCell}>
                          <div className={styles.priceWrapper}>
                            <FontAwesomeIcon icon={faCoins} className={styles.currencyIcon} />
                            {item.price > 0 ? item.price.toLocaleString() : 'Free'}
                          </div>
                        </td>
                        <td className={styles.actionCell}>
                          <button
                            className={styles.buyButton}
                            onClick={() => handleBuyItem(item)}
                            disabled={item.stock === 0 || (mercenary?.silver || 0) < item.price}
                            title={(mercenary?.silver || 0) < item.price ? 'Not enough silver' : 'Purchase Item'}
                          >
                            Buy
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles.emptyState}>
                {selectedVendor ? 'This vendor has no goods in this category.' : ''}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Marketplace;
